import { frappeList, frappeGet } from "./utils";
import { getERPClient } from "./client";
import type { FrappeListResponse } from "@/lib/frappe-client";
import type { FrappeFilterArray } from "./filterMapper";
import { docTypeListWithPagination } from "./docs";

/**
 * Default fields to fetch for Pricing Rule list views
 *
 * This constant ensures consistency across:
 * - pricingRules.list() (legacy, if needed)
 * - pricingRules.listWithPagination() defaultFields
 * - usePricingRuleListWithPagination fields
 * - createPricingRuleColumns accessorKeys
 *
 * IMPORTANT:
 * - If you add a new column to createPricingRuleColumns, add the corresponding field here.
 * - Only include real database fields (not computed fields).
 */
export const PRICING_RULE_LIST_FIELDS = [
  "name",
  "title",
  "apply_on",
  "applicable_for",
  "price_or_product_discount",
  "rate_or_discount",
  "discount_percentage",
  "rate",
  "valid_from",
  "valid_upto",
  "priority",
  "customer",
  "customer_group",
  "item_code",
  "item_group",
  "brand",
  "warehouse",
  "company",
  "currency",
  "selling",
  "buying",
] as const;

export interface PricingRuleListParams {
  /**
   * Pre-mapped Frappe filter array, e.g.:
   * [["Pricing Rule", "apply_on", "=", "Item Code"]]
   */
  filters?: FrappeFilterArray;
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  fields?: string[];
}

export interface PricingRule {
  name: string;
  title?: string;
  apply_on?: string;
  applicable_for?: string;
  price_or_product_discount?: string;
  rate_or_discount?: string;
  discount_percentage?: number;
  rate?: number;
  valid_from?: string;
  valid_upto?: string;
  priority?: number;
  customer?: string;
  customer_group?: string;
  item_code?: string;
  item_group?: string;
  brand?: string;
  warehouse?: string;
  company?: string;
  currency?: string;
  selling?: 0 | 1;
  buying?: 0 | 1;
  // Allow additional backend fields without breaking typing
  [key: string]: any;
}

export const pricingRules = {
  /**
   * List all pricing rules (legacy method - returns array only)
   * @deprecated Use listWithPagination for server-side pagination
   */
  list(filters?: Record<string, any>): Promise<PricingRule[]> {
    return frappeList<PricingRule>(
      "Pricing Rule",
      filters,
      [...PRICING_RULE_LIST_FIELDS]
    );
  },

  /**
   * List pricing rules with server-side pagination and filtering.
   *
   * This uses the generic docTypeListWithPagination path without
   * any special logic.
   */
  listWithPagination(
    params: PricingRuleListParams
  ): Promise<FrappeListResponse<PricingRule>> {
    const {
      filters = [],
      pageIndex = 0,
      pageSize = 20,
      orderBy,
      fields,
    } = params;

    const defaultFields = fields ?? [...PRICING_RULE_LIST_FIELDS];

    return docTypeListWithPagination<PricingRule>({
      doctype: "Pricing Rule",
      filters,
      pageIndex,
      pageSize,
      orderBy: orderBy ?? "modified desc",
      fields: defaultFields,
    });
  },

  /**
   * Get a single pricing rule by name
   */
  detail(name: string): Promise<PricingRule> {
    return frappeGet<PricingRule>("Pricing Rule", name);
  },

  /**
   * Create a new pricing rule
   */
  create(data: Record<string, any>): Promise<PricingRule> {
    const client = getERPClient();
    return client.create({ doctype: "Pricing Rule", data });
  },

  /**
   * Update an existing pricing rule
   */
  update(name: string, data: Record<string, any>): Promise<PricingRule> {
    const client = getERPClient();
    return client.update({ doctype: "Pricing Rule", name, data });
  },

  /**
   * Delete a pricing rule
   */
  delete(name: string): Promise<void> {
    const client = getERPClient();
    return client.delete({ doctype: "Pricing Rule", name });
  },
};

